import axios from "axios";
import { VITE_API_V2_URL } from "codes";
import { isLocalhost } from "helpers/isLocalhost";
import { getDeviceID } from "lib/webPush";
import { version } from "../package.json";

const isPrivateBrowsing = window.matchMedia("(private-browsing: true)").matches;

const env = import.meta.env.VITE_ENV;
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;
const debug = Boolean(env === "local" || env === "dev");

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

function attachUpdateHandler(
  registration: ServiceWorkerRegistration,
  config?: Config,
) {
  registration.onupdatefound = () => {
    const installingWorker = registration.installing;
    if (!installingWorker) return;

    installingWorker.onstatechange = () => {
      if (installingWorker.state !== "installed") return;

      if (navigator.serviceWorker.controller) config?.onUpdate?.(registration);
      else config?.onSuccess?.(registration);
    };
  };
}

function registerValidSW(swUrl: string, config?: Config) {
  if ("serviceWorker" in navigator) {
    navigator?.serviceWorker
      .getRegistration(swUrl)
      .then((registration) => {
        if (registration) {
          // eslint-disable-next-line no-unused-expressions
          debug &&
            // eslint-disable-next-line no-console
            console.log("ServiceWorker is already registered");
          attachUpdateHandler(registration, config);
          if (registration.waiting && navigator.serviceWorker.controller) {
            config?.onUpdate?.(registration);
          }
          registration.update().catch(() => undefined);
        } else {
          navigator?.serviceWorker
            // eslint-disable-next-line no-shadow
            .register(swUrl)
            // eslint-disable-next-line no-shadow
            .then((registration) => {
              attachUpdateHandler(registration, config);
              // eslint-disable-next-line no-console
              console.log("ServiceWorker registered:", registration);
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error("Error during service worker registration:", error);
            });
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error checking service worker registration:", error);
      });
  }
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  if (!isPrivateBrowsing)
    axios
      .get(swUrl, {
        headers: { "Service-Worker": "script" },
      })
      .then((response) => {
        // Ensure service worker exists, and that we really are getting a JS file.
        const contentType = response.headers["content-type"];
        if (
          response.status === 404 ||
          (contentType != null && contentType.indexOf("javascript") === -1)
        ) {
          // No service worker found. Probably a different app. Reload the page.
          navigator?.serviceWorker.ready.then((registration) => {
            registration.unregister().then(() => {
              window.location.reload();
            });
          });
        } else {
          // Service worker found. Proceed as normal.
          registerValidSW(swUrl, config);
        }
      })
      .catch(() => {
        // eslint-disable-next-line no-unused-expressions
        debug &&
          // eslint-disable-next-line no-console
          console.log(
            "No internet connection found. App is running in offline mode."
          );
      });
}

export function register(config?: Config) {
  if (!isPrivateBrowsing)
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        const swUrl = "/sw.js"; // Ensure the correct path to the service worker file
        const LOCALHOST = "https://127.0.0.1:5173/sw.js";
        if (isLocalhost) {
          // This is running on localhost. Let's check if a service worker still exists or not.
          checkValidServiceWorker(`${LOCALHOST}${swUrl}`, config);
          navigator?.serviceWorker.ready.then(
            (registration: ServiceWorkerRegistration) => {
              // eslint-disable-next-line no-unused-expressions
              debug &&
                // eslint-disable-next-line no-console
                console.log(
                  "Service Worker registration successful with scope: ",
                  registration.scope
                );
            }
          );
        } else {
          // Is not localhost. Just register service worker
          registerValidSW(swUrl, config);
        }
      });
    }
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator?.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
  }
}

export const registerWorker = () => {
  if (!isPrivateBrowsing)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register(`/sw.js?v=${version}`)
        .then((registration) => {
          if (debug === true)
            //eslint-disable-next-line no-console
            console.log(
              "Service Worker registered with scope:",
              registration.scope
            );
        })
        .catch((error) => {
          if (debug === true)
            //eslint-disable-next-line no-console
            console.error("Service Worker registration failed:", error);
        });
    } else {
      // eslint-disable-next-line no-unused-expressions
      if (debug === true)
        //eslint-disable-next-line no-console
        console.log("No service worker in navigator");
      return undefined;
    }
};

export async function subscribeUser(contact_id: string) {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: VAPID_PUBLIC_KEY,
    });
    const JSONSubscription = subscription.toJSON();
    const body = {
      endpoint: JSONSubscription.endpoint,
      keys: JSONSubscription.keys,
      device_id: getDeviceID(),
      contact_id,
    };

    const response = await fetch(`${VITE_API_V2_URL}/subscribe`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (debug === true)
        //eslint-disable-next-line no-console
        console.error("Failed to subscribe the user.");
    }
  } else {
    //eslint-disable-next-line no-console
    console.error("Service workers are not supported.");
  }
}
